import React, { FC } from 'react'
import { useHistory } from 'react-router-dom'
import { FormProvider, useForm, useFieldArray } from 'react-hook-form'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useDispatch } from 'react-redux'
import { CreateCrmRuleRequestDto } from '@sparling/financial-common'
import { TransparentButton } from '../../../../../components/Buttons/TransparentButton'
import { GreenButton } from '../../../../../components/Buttons/GreenButton'
import { InputField } from '../../../../../components/InputField'
import { InputSelect } from '../../../../../components/InputSelect'
import {
  riskScoreTypes,
  riskScoreOperators,
  riskScoreLevels,
  finalRiskScoreTypes,
} from '../../../../../constants/riskScoreRuleOptions'
import { createRiskScoreRuleRequest } from '../../../../../store/actions/crm-rules'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
  },
  title: {
    lineHeight: 0,
  },
  sectionWrap: {
    width: '100%',
    margin: '32px 0',
    padding: theme.spacing(3),
    background: 'rgba(216, 216, 216, 0.05)',
  },
  inputWrap: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  buttonWrap: {
    justifyContent: 'space-between',
    width: 256,
  },
  sectionBox: {
    border: '1px solid #767a7e',
    padding: theme.spacing(3),
    marginTop: theme.spacing(4),
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  headerRow: {
    color: '#9b9c9f',
    marginBottom: theme.spacing(1),
  },
  rowWrap: {
    alignItems: 'center',
  },
  dragIcon: {
    width: 24,
    height: 24,
    borderRadius: 4,
    background: 'rgba(216,216,216,0.2)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    color: '#9b9c9f',
    marginLeft: theme.spacing(1),
  },
  addMoreWrap: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    color: '#9b9c9f',
  },
  actionsBottom: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
}))

export const CreateRule: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()

  const type0 = riskScoreTypes[0]?.value || ''
  const op0 = riskScoreOperators[0]?.value || ''
  const level0 = riskScoreLevels[0]?.value || ''
  const defaultWhenItem = { type: type0, operator: op0, level: level0 }
  const defaultThenItem = {
    type: riskScoreTypes[0]?.value,
    operator: riskScoreOperators[0]?.value,
    level: riskScoreLevels[0]?.value,
  }

  const formMethods = useForm({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      when: [defaultWhenItem],
      then: [defaultThenItem],
    },
  })
  const { handleSubmit, control } = formMethods

  const whenArray = useFieldArray({ control, name: 'when' })
  const thenArray = useFieldArray({ control, name: 'then' })

  const onSubmit = (data: CreateCrmRuleRequestDto) => {
    dispatch(createRiskScoreRuleRequest(data))
    history.goBack()
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.topWrap}>
          <Typography variant="h1" className={classes.title}>
            Create Rule
          </Typography>
          <Grid container item className={classes.buttonWrap}>
            <TransparentButton width={120} onClick={history.goBack} text="Cancel" />
            <GreenButton type="submit" width={120} text="Save" />
          </Grid>
        </Grid>

        {/* Rule Name */}
        <Grid item container className={classes.sectionWrap}>
          <Grid item container className={classes.inputWrap}>
            <InputField inputName={'name'} labelName={'Rule Name'} inputType={'text'} fullWidth />
          </Grid>
        </Grid>

        {/* When section */}
        <Grid item container className={classes.sectionWrap}>
          <Typography variant="h5" className={classes.sectionHeader}>
            When
          </Typography>

          {/* One-time headers */}
          <Grid container className={classes.headerRow} spacing={2}>
            <Grid item xs={5}>
              <Typography variant="body2">Score Type</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2">Operator</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2">Score Level</Typography>
            </Grid>
          </Grid>

          {whenArray.fields.map((field, index) => (
            <Grid key={field.id} container className={classes.rowWrap} spacing={2}>
              <Grid item xs={5}>
                <InputSelect
                  inputName={`when[${index}].type`}
                  fullWidth
                  enums={riskScoreTypes}
                  defaultValue={'Client risk score'}
                />
              </Grid>

              <Grid item xs={3}>
                <InputSelect
                  inputName={`when[${index}].operator`}
                  fullWidth
                  enums={riskScoreOperators}
                  defaultValue={'equal to'}
                />
              </Grid>

              <Grid item xs={4}>
                <InputSelect
                  inputName={`when[${index}].level`}
                  fullWidth
                  enums={riskScoreLevels}
                  defaultValue={'High'}
                />
              </Grid>
            </Grid>
          ))}

          <Grid container className={classes.rowWrap}>
            <div className={classes.addMoreWrap}>
              <Typography>Condition</Typography>
              <TransparentButton
                width={160}
                text="Add Row"
                padding="20px"
                onClick={() => whenArray.append(defaultWhenItem)}
              />
            </div>
          </Grid>
        </Grid>

        {/* Then section */}
        <Grid item container className={classes.sectionWrap}>
          <Typography variant="h5" className={classes.sectionHeader}>
            Then
          </Typography>

          {/* One-time headers */}
          <Grid container className={classes.headerRow} spacing={2}>
            <Grid item xs={5}>
              <Typography variant="body2">Action Type</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2">Operator</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2">Status</Typography>
            </Grid>
          </Grid>

          {thenArray.fields.map((field, index) => (
            <Grid key={field.id} container className={classes.rowWrap} spacing={2}>
              <Grid item xs={5}>
                <InputSelect
                  inputName={`then[${index}].type`}
                  fullWidth
                  enums={finalRiskScoreTypes}
                  defaultValue={'Client risk score'}
                />
              </Grid>

              <Grid item xs={3}>
                <InputSelect
                  inputName={`then[${index}].operator`}
                  fullWidth
                  enums={riskScoreOperators}
                  defaultValue={'equal to'}
                />
              </Grid>
              <Grid item xs={4}>
                <InputSelect
                  inputName={`then[${index}].level`}
                  fullWidth
                  enums={riskScoreLevels}
                  defaultValue={'High'}
                />
              </Grid>
            </Grid>
          ))}

          <Grid container className={classes.rowWrap}>
            <div className={classes.addMoreWrap}>
              <Typography>Condition</Typography>
              <TransparentButton
                width={160}
                text="Add Row"
                padding="20px"
                onClick={() => thenArray.append(defaultThenItem)}
              />
            </div>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
